namespace appn.designerv2 {
    export let designer: DesignerBase;

    // disable scroll restoration (browser remembering scroll position) for designer scenarios
    if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
    }
    
    export abstract class DesignerBase {
        _canSave: boolean = false;
        _design!: IDesign;
        _selectedComponentId!: string | null;
        _designerJSONFieldName!: string;
        private _rootSnapshots: Map<string, IRootSnapshot> = new Map();
        private _pathGuards: Map<string, object> = new Map();

        run(args: IDesignerArgs) {
            this.init(args);
            this.bindEvents();
            this.fetchAndLoadDesign(args);
        }
        
        protected init(args: IDesignerArgs): void {
            designer = this;
            this._canSave = args.canSave;

            autosave.init(args.canSave);
            propertyPane.init('.propertyList', args.canSave);
            
            if (!canvas)
                throw new Error('Canvas needs to be assigned prior to init being called');
            
            canvas.init(!args.canSave);
            
            toolPane.init();
            
            this._designerJSONFieldName = args.designerJSONFieldName;

            $('.info-box-icon').smallipop({
                preferredPosition: 'right',
                theme: 'white info-box-icon',
                popupOffset: 0,
            });

            if (!this._canSave) {
                // Skips event binding for logic safety if designer is read-only
                // Periodically check and disable properties as they are rendered dynamically
                eventBus.subscribe(EVENTS.COMPONENT_SELECTED, () => {
                    setTimeout(() => this.setPropsToDisabledAndReadOnly(), 50);
                });
            }
        }
        
        protected fetchAndLoadDesign(args: IDesignerArgs) {
            const savedDesign = autosave.getSavedDesign();
            const initialDesign = $('#designDef').data('def') || this.createDefaultDesign(args.name);

            if (!savedDesign || !args.canSave) {
                this.render(initialDesign);
                return;
            }

            appn.dialog.confirm({
                modalColor: 'modal-info',
                title: T('Load from Auto Save'),
                message: T('An auto-saved version of this design was found. Load this instead?'),
                callback: function (result: any) {
                    if (result) {
                        designer.render(savedDesign);
                    } else {
                        autosave.clear(); // User chose not to load, so clear it
                        designer.render(initialDesign);
                    }
                }
            });
        }
        
        protected render(design: IDesign) {
            this.setDesign(design);
            
            canvas.render(design);

            const selectedId = this._selectedComponentId;
            if (selectedId) {
                this.setSelectedComponent(selectedId, true);
                $(`#${selectedId}`).addClass('selected');
            } else {
                const firstComponent = this._design.components.find(c => c.type);
                if (firstComponent) {
                    this.setSelectedComponent(firstComponent.id, true);
                    $(`#${firstComponent.id}`).addClass('selected');
                }
            }

            const getFlatComponents = (components: IComponent[]): IComponent[] => {
                return components.reduce((acc, comp) => {
                    acc.push(comp);
                    if (comp.children)
                        acc.push(...getFlatComponents(comp.children));

                    return acc;
                }, [] as IComponent[]);
            };

            getFlatComponents(design.components).forEach(component => {
                const definition = componentManager.getDefinition(component.type);
                if (definition) {
                    const dataQueryPropNames = componentManager.getDataQueryPropertyNames(definition);
                    if (dataQueryPropNames.length > 0)
                        eventBus.publish(EVENTS.DATA_REFRESH_REQUESTED, { componentId: component.id });
                }
            });

            $('.info-box-icon').smallipop({
                preferredPosition: 'right',
                theme: 'white info-box-icon',
                popupOffset: 0,
            });
        }

        protected abstract createDefaultDesign(name: string): IDesign;
        
        // Event handling
        protected bindEvents(): void {
            $('body').on('click', '.focus-component', (e) => {
                e.preventDefault();
                const componentId = $(e.currentTarget).data('component-id');
                this.setSelectedComponent(componentId);
                appn.dialog.hide();
            });

            $('body').on('click', '#saveLink, .btn[name="submitType"]', function (e) {
                e.preventDefault();

                designer.submit($(this).val() as string || 'Save');
            });
            
            $('.workspace').on('click', '.component.widget, .component.titleBar', function (e) {
                e.stopPropagation();
                const componentId = $(this).attr('id');
                if (componentId) {
                    $('.component.selected').removeClass('selected');
                    $(this).addClass('selected');
                    designer.setSelectedComponent(componentId);
                }
            });
            
            $('.workspace').on('click', '.deleteComponent', function (e) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                const componentId = $(this).closest('.component').attr('id');
                if (componentId) {
                    const handled = canvas.removeComponent(componentId);
                    if (!handled)
                        designer.deleteComponent(componentId);
                }
                return false;
            });
        }

        submit(submitType: string): void {
            const validationErrors = this.validateDesign();
            if (validationErrors.length > 0) {
                let message = `<p>${T('The design has validation errors. Please correct them before saving.')}</p>`;
                message += '<ul class="validation-errors-list" style="max-height: 200px; overflow-y: auto; text-align: left;">';
                
                validationErrors.forEach(err => {
                    const component = this.getComponent(err.componentId);
                    const definition = component ? componentManager.getDefinition(component.type) : null;
                    const propDef = definition ? definition.properties[err.propName] : null;
                    const propDisplayName = propDef ? propDef.name : err.propName;
                    const componentName = component?.name || component?.id || T('Unknown Component');
                    
                    err.errors.forEach(errorMsg => {
                        message += `<li><a href="#" class="focus-component" data-component-id="${err.componentId}">${componentName} (${propDisplayName}): ${T(errorMsg)}</a></li>`;
                    });
                });
                message += '</ul>';

                appn.dialog.custom({
                    title: T('Cannot Save'),
                    message: message,
                    modalColor: 'modal-danger',
                    buttons: {
                        ok: {
                            label: T('OK'),
                            className: 'btn-danger'
                        }
                    }
                });
                return;
            }

            const design = this.getDesignForSave();
            const jsonString = JSON.stringify(design);
            const $form = $('form#appnForm');
            
            $form.find(`input[name="submitType"], input[name="${this._designerJSONFieldName}"]`).remove();
            $form.append($('<input type="hidden" name="submitType" />').val(submitType));
            $form.append($(`<input type="hidden" name="${this._designerJSONFieldName}" />`).val(jsonString));

            autosave.clear();
            $form.submit();
        }
        
        // State accessors
        setDesign(design: IDesign): void {
            const ensureDefaults = (components: IComponent[]) => {
                components.forEach(c => {
                    this.onEnsureDefaults(c);
                    if (c.children)
                        ensureDefaults(c.children);
                });
            };

            ensureDefaults(design.components);

            this.normalizeLabelFromDataName(design);
            this._design = design;

            this.captureSnapshots(design.components);

            eventBus.publish(EVENTS.STATE_CHANGED, {design});
        }

        // Walks the component tree once so concrete designers can snapshot what they need, per component
        protected captureSnapshots(components: IComponent[]): void {
            components.forEach(component => {
                this.captureComponentSnapshots(component);

                if (component.children)
                    this.captureSnapshots(component.children);
            });
        }

        // Overridable: capture any root snapshots this component needs to stay restorable
        protected captureComponentSnapshots(component: IComponent): void { }
        
        // can be overridden by concrete designers for additional defaulting
        onEnsureDefaults(component: IComponent) {
            if (!component.id)
                component.id = appn.utils.newGuid(true);

            component.id = appn.utils.normalizeIdentifier(component.id);
        }

        getDesignForSave(): IDesign | null {
            if (!this._design)
                return null;

            return appn.utils.deepClone(this._design);
        }

        // Several editors can share one root property (e.g. every data-query editor writes "dataQuery0").
        // A snapshot captures how a root was loaded so it can be restored; a guard makes a property path
        // read-only for every editor except its holder, so a read-only path cannot be corrupted by hidden editors.

        snapshotRoot(componentId: string, rootPropName: string): IRootSnapshot {
            const component = this.getComponent(componentId);
            const captured = component ? appn.utils.deepClone((component as any)[rootPropName]) : undefined;

            const snapshot: IRootSnapshot = {
                value: appn.utils.deepClone(captured),
                restore: () => {
                    if (!this.getComponent(componentId))
                        return;

                    this.updateComponentProperty(componentId, rootPropName, appn.utils.deepClone(captured));
                }
            };

            this._rootSnapshots.set(this.stateKey(componentId, rootPropName), snapshot);
            return snapshot;
        }

        getRootSnapshot(componentId: string, rootPropName: string): IRootSnapshot | null {
            return this._rootSnapshots.get(this.stateKey(componentId, rootPropName)) || null;
        }

        guardPath(componentId: string, path: string, holder: object): IPathGuard {
            const key = this.stateKey(componentId, path);
            this._pathGuards.set(key, holder);

            return {
                release: () => {
                    if (this._pathGuards.get(key) === holder)
                        this._pathGuards.delete(key);
                }
            };
        }

        canWritePath(componentId: string, path: string, holder: object): boolean {
            const prefix = `${componentId}::`;

            for (const [key, guardedBy] of this._pathGuards) {
                if (!key.startsWith(prefix))
                    continue;

                // A guard covers its own path and everything beneath it
                const guardedPath = key.slice(prefix.length);
                if (guardedBy !== holder && (path === guardedPath || path.startsWith(`${guardedPath}.`)))
                    return false;
            }

            return true;
        }

        private stateKey(componentId: string, path: string): string {
            return `${componentId}::${path}`;
        }

        getComponent(componentId: string | null): IComponent | null {
            if (!this._design || !componentId)
                return null;

            const normalizedId = appn.utils.normalizeIdentifier(componentId);
            
            const find = (components: IComponent[]): IComponent | null => {
                for (const c of components) {
                    if (c.id === normalizedId)
                        return c;

                    if (c.children) {
                        const found = find(c.children);
                        if (found)
                            return found;
                    }
                }

                return null;
            };

            return find(this._design.components);
        }

        validateDesign(): IValidationError[] {
            const allErrors: IValidationError[] = [];

            const validateComponents = (components: IComponent[]) => {
                for (const component of components) {
                    const definition = componentManager.getDefinition(component.type);
                    if (definition) {
                        for (const propName in definition.properties) {
                            const propDef = definition.properties[propName];
                            if (!propDef.limit)
                                continue;
                            
                            let value = (component as any)[propName] ?? propDef.defaultValue;
                            const errors = editors.validateValue(value, propDef.limit);
                            if (errors.length <= 0)
                                continue;
                            
                            console.warn(`Validation error in component ${component.id}, property ${propName}:`, errors);
                            const errorObj = {componentId: component.id, propName: propName, errors: errors};
                            allErrors.push(errorObj);
                            eventBus.publish(EVENTS.ERROR_OCCURRED, errorObj);
                        }
                    }

                    if (component.children)
                        validateComponents(component.children);
                }
            };

            if (this._design && this._design.components) {
                validateComponents(this._design.components);
            }

            return allErrors;
        }

        getSelectedComponent(): IComponent | null {
            return this._selectedComponentId ? this.getComponent(this._selectedComponentId) : null;
        }
        
        setSelectedComponent(componentId: string | null, forcePublish?: boolean, onlySector?: string): void {
            const normalizedId = componentId ? appn.utils.normalizeIdentifier(componentId) : null;
            if (this._selectedComponentId === normalizedId && !forcePublish)
                return;

            this._selectedComponentId = normalizedId;

            eventBus.publish(EVENTS.COMPONENT_SELECTED, { componentId: normalizedId, onlySector: onlySector });
        }

        addComponent(componentType: string, position: IGridPosition): IComponent | null {
            const newComponent = componentManager.createComponent(componentType);
            if (!newComponent || !this._design)
                return null;

            newComponent.id = appn.utils.normalizeIdentifier(newComponent.id);
            newComponent.gridPosition = position;

            const group = this._design.components.find((c: IComponent) => c.type === 'group');
            if (group && group.children)
                group.children.push(newComponent);
            else
                this._design.components.push(newComponent);

            this.assignUniqueNameAndLabel(newComponent);

            autosave.markDirty();
            eventBus.publish(EVENTS.STATE_CHANGED, {design: this._design});

            return newComponent;
        }
        
        deleteComponent(componentId: string): void {
            if (!this._design)
                return;

            const normalizedId = appn.utils.normalizeIdentifier(componentId);
            const findAndDelete = (components: IComponent[]): boolean => {
                const index = components.findIndex(c => c.id === normalizedId);
                if (index > -1) {
                    components.splice(index, 1);
                    return true;
                }
                for (const c of components) {
                    if (c.children && findAndDelete(c.children))
                        return true;
                }

                return false;
            };

            if (findAndDelete(this._design.components)) {
                if (this._selectedComponentId === normalizedId)
                    this.setSelectedComponent(null);

                autosave.markDirty();
                eventBus.publish(EVENTS.STATE_CHANGED, {design: this._design});
            }
        }
        
        updateComponentProperty(componentId: string, propName: string, propValue: unknown, propPath?: string): void {
            const component = this.getComponent(componentId);
            if (!component)
                return;

            // Determine current (old) values
            const oldRoot = (component as any)[propName];
            let oldLeaf = oldRoot;
            let newLeaf = propValue as any;

            if (propPath) {
                const pathParts = propPath.split('.');
                const rootName = pathParts[0];
                const pathWithinObject = pathParts.slice(1).join('.');

                // Sanity: only treat as path under the same root
                if (rootName === propName && pathWithinObject) {
                    oldLeaf = appn.designerv2.getPropertyByPath(oldRoot, pathWithinObject);
                    // propValue is expected to be the new root object; derive the leaf value from it for comparisons/events
                    newLeaf = appn.designerv2.getPropertyByPath(propValue, pathWithinObject);
                }
            }

            // If no effective change at the leaf level, bail out early
            try {
                const oldStr = JSON.stringify(oldLeaf);
                const newStr = JSON.stringify(newLeaf);
                if (oldStr === newStr)
                    return;
            } catch {
                // Fallback to strict equality if values aren't serializable
                if (oldLeaf === newLeaf)
                    return;
            }

            // Apply new value (always set the root property; callers pass full root when propPath is used)
            (component as any)[propName] = propValue;

            // allow concrete designers the chance to react to change in prop value
            // For path-based updates, pass the ROOT old object to the handler
            const handlerOldValue = propPath ? oldRoot : oldLeaf;
            if (!this.onUpdateComponentProperty(component, propName, handlerOldValue, propPath))
                return;

            autosave.markDirty();
            // Publish the update; include propPath so listeners can react to leaf changes
            eventBus.propertyUpdated(component.id, propName, newLeaf, oldLeaf, propPath);
            eventBus.publish(EVENTS.STATE_CHANGED, {design: this._design});
        }
        
        abstract onUpdateComponentProperty(component: IComponent, propName: string, oldValue: any, propPath?: string): boolean;

        updateComponentPosition(componentId: string, position: IGridPosition): void {
            const component = this.getComponent(componentId);
            if (component) {
                component.gridPosition = position;
                autosave.markDirty();
                eventBus.publish(EVENTS.STATE_CHANGED, {design: this._design});
            }
        }
        
        assignUniqueNameAndLabel(component: IComponent): void {
            if (!component || !this._design)
                return;

            if (component.type === appn.designerv2.BASE_COMPONENT_TYPE.GROUP)
                return;

            const def = componentManager.getDefinition(component.type);
            const baseTitle = (def?.title || 'Action').toString();

            const existingLabels: string[] = [];
            const stack: IComponent[] = Array.isArray(this._design.components) ? this._design.components.slice() : [];
            while (stack.length) {
                const c = stack.pop()!;
                if (c && c.id !== component.id && c.type !== appn.designerv2.BASE_COMPONENT_TYPE.GROUP) {
                    const label = c.label?.eng?.trim();
                    if (label && label.length) 
                        existingLabels.push(label);
                    else {
                        const d = componentManager.getDefinition(c.type);
                        if (d?.title) existingLabels.push(d.title.toString());
                    }
                }
                
                const ch = c?.children;
                if (Array.isArray(ch) && ch.length) 
                    stack.push(...ch);
            }

            let max = 0;
            let hasBase = false;
            for (const t of existingLabels) {
                if (!t.startsWith(baseTitle)) 
                    continue;
                if (t === baseTitle) { 
                    hasBase = true; 
                    max = Math.max(max, 1); 
                    continue; 
                }
                const m = t.match(/(\d+)$/);
                if (m) {
                    const n = parseInt(m[1], 10);
                    if (!isNaN(n)) max = Math.max(max, n);
                }
            }

            const finalTitle = (max > 0 || hasBase) ? `${baseTitle} ${max + 1}` : baseTitle;
            const dataName = this.toCamelCase(finalTitle);

            this.updateComponentProperty(component.id, 'label', { eng: finalTitle });
            this.updateComponentProperty(component.id, 'name', dataName);
        }
        
        normalizeLabelFromDataName(design: IDesign): void {
            const stack: IComponent[] = Array.isArray(design.components) ? design.components.slice() : [];
            while (stack.length) {
                const c = stack.pop()!;
                if (!c || c.type === appn.designerv2.BASE_COMPONENT_TYPE.GROUP) {
                    const chSkip = c?.children;
                    if (Array.isArray(chSkip) && chSkip.length) stack.push(...chSkip);
                    continue;
                }

                const def = componentManager.getDefinition(c.type);
                const baseTitle = (def?.title || '').toString();
                const currentLabel = c.label?.eng?.trim() || '';
                const dataName = c.name?.trim();

                // Only normalise label if it's empty OR if it's the default baseTitle
                // Don't overwrite user-customised labels
                const isDefaultLabel = currentLabel === '' || (baseTitle && currentLabel === baseTitle);
                if (isDefaultLabel && baseTitle) {
                    if (!c.label)
                        c.label = { eng: '' };
                    
                    if (dataName) {
                        const m = dataName.match(/(\d+)$/);
                        const num = m ? parseInt(m[1], 10) : null;
                        c.label.eng = num ? `${baseTitle} ${num}` : baseTitle;
                    } 
                    else {
                        c.label.eng = baseTitle;
                    }
                }

                const ch = c?.children;
                if (Array.isArray(ch) && ch.length) 
                    stack.push(...ch);
            }
        }

        toCamelCase(str: string): string {
            return (str || '')
                .replace(/[^a-zA-Z0-9 ]/g, '')
                .split(' ')
                .map((w, i) => i === 0 ? w.toLowerCase() : (w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
                .join('');
        }

        protected setPropsToDisabledAndReadOnly() {
            this.setPropsToReadonly();
            this.setPropsToDisabled();
        }

        private setPropsToReadonly() {
            // Don't allow fields to be editable if model is read only
            // READONLY - Allows text copy
            $('.propertyList').find('input[type="text"]:not(.js-no-disable),input[type="number"]:not(.js-no-disable),textarea:not(.js-no-disable)').each(function () {
                $(this).attr('readonly', 'readonly');
            });
        }

        private setPropsToDisabled() {
            // Checkboxes, radios, selects and buttons should be disabled
            $('.propertyList').find('input[type="radio"]:not(.js-no-disable),.displayButton:not(.js-no-disable),input[type="text"].colorPicker:not(.js-no-disable),input[type="text"].color:not(.js-no-disable),input[type="checkbox"]:not(.js-no-disable),select:not(.js-no-disable):not(#pageSelect):not(#SelectedDesignerLanguage),button:not(.js-no-disable),input[type="range"]:not(.js-no-disable),.buildIcon:not(.js-no-disable),a.btn:not(.js-no-disable),.removeLink:not(.js-no-disable),.removeBtn:not(.js-no-disable),.remove-projection:not(.js-no-disable),.remove-metric:not(.js-no-disable),.remove-sort-order:not(.js-no-disable),.remove-ref:not(.js-no-disable),.removeGroupBtn:not(.js-no-disable),.addCriterionToGroupBtn:not(.js-no-disable)').each(function () {
                $(this).attr('disabled', 'disabled');
            });

            // Handle anchors that act as buttons (Distill Dashboard designer uses these)
            $('.propertyList').find('a.btn:not(.js-no-disable),.removeLink:not(.js-no-disable),.removeBtn:not(.js-no-disable),.remove-projection:not(.js-no-disable),.remove-metric:not(.js-no-disable),.remove-sort-order:not(.js-no-disable),.remove-ref:not(.js-no-disable),.removeGroupBtn:not(.js-no-disable),.addCriterionToGroupBtn:not(.js-no-disable)').each(function () {
                $(this).addClass('disabled').attr('aria-disabled', 'true').on('click.disabled', (e) => e.preventDefault());
            });

            // Handle specialized components like Minicolors or Select2 if they are present in propertyPane
            $('.propertyList').find('.minicolors-input:not(.js-no-disable)').each(function() {
                try { ($(this) as any).minicolors('settings', { readOnly: true }); } catch {}
            });
        }
    }
    
    // Placeholder designer class to ensure designer is initialised to something concrete
    // Necessary to prevent TS compiler from optimising away the designer var.
    class PlaceholderDesigner extends DesignerBase {
        protected createDefaultDesign(name: string): IDesign {
            throw new Error('Dummy designer in use?');
        }
        onUpdateComponentProperty(component: IComponent, propName: string, oldValue: any, propPath?: string): boolean {
            return false;
        }
    }
    designer = new PlaceholderDesigner();
}
